import React, { useState, useEffect, useMemo } from "react";
import { useTheme } from "next-themes";
import {
  CheckCircle2, XCircle, MinusCircle,
  Shield, AlertTriangle, ArrowLeft, Loader2, ChevronDown, Search, X,
} from "lucide-react";
import { type ArchApplicationListItem } from "../../api/frameworksApi";

// ─── Types ───────────────────────────────────────────────────────────────────
type ControlStatus = "pass" | "fail" | "na";
interface ArchAssetSummary { pass: number; fail: number; na: number; total: number; passRate: number; }
interface ControlItem { control_id: string; control_status: ControlStatus; evidence?: string; }
interface ServiceGroup { service: string; pass: number; fail: number; na: number; total: number; controls: ControlItem[]; }
interface ScanInfo { lastScanTime: string; catalogName: string; accountId: string; sspName: string; }

// ─── Dummy Data (no API) ──────────────────────────────────────────────────────
const DUMMY_APPS: ArchApplicationListItem[] = [
  { applicationId: "app-001", applicationName: "Payments Platform", description: "Core payment processing services", cloudAccountCount: 3, providers: ["AWS"] },
  { applicationId: "app-002", applicationName: "Customer Portal", description: "Customer-facing web application", cloudAccountCount: 2, providers: ["AWS"] },
  { applicationId: "app-003", applicationName: "Analytics Pipeline", description: "Data ingestion and reporting", cloudAccountCount: 1, providers: ["AWS"] },
];

const DUMMY_CONTROLS: Record<string, ControlItem[]> = {
  "app-001": [
    { control_id: "CIS-IAM-1.1", control_status: "pass", evidence: "MFA enabled on root account." },
    { control_id: "CIS-IAM-1.2", control_status: "fail", evidence: "IAM password policy missing complexity rules." },
    { control_id: "CIS-IAM-1.3", control_status: "fail", evidence: "Unused access keys older than 90 days detected." },
    { control_id: "CIS-IAM-1.4", control_status: "pass", evidence: "No root access keys present." },
    { control_id: "CIS-S3-2.1", control_status: "pass", evidence: "All buckets enforce SSL requests." },
    { control_id: "CIS-S3-2.2", control_status: "fail", evidence: "Public access block disabled on one bucket." },
    { control_id: "CIS-S3-2.3", control_status: "na", evidence: "No static website buckets in scope." },
    { control_id: "CIS-EC2-3.1", control_status: "pass", evidence: "EBS volumes encrypted at rest." },
    { control_id: "CIS-EC2-3.2", control_status: "fail", evidence: "Security group allows 0.0.0.0/0 on port 22." },
    { control_id: "CIS-EC2-3.3", control_status: "pass", evidence: "Default security groups restrict all traffic." },
    { control_id: "CIS-CLOUDTRAIL-4.1", control_status: "pass", evidence: "CloudTrail enabled in all regions." },
    { control_id: "CIS-CLOUDTRAIL-4.2", control_status: "pass", evidence: "Log file validation enabled." },
    { control_id: "CIS-RDS-5.1", control_status: "fail", evidence: "RDS instance publicly accessible." },
    { control_id: "CIS-RDS-5.2", control_status: "pass", evidence: "Automated backups enabled." },
  ],
  "app-002": [
    { control_id: "CIS-IAM-1.1", control_status: "pass", evidence: "MFA enabled on root account." },
    { control_id: "CIS-IAM-1.2", control_status: "pass", evidence: "Strong IAM password policy enforced." },
    { control_id: "CIS-S3-2.1", control_status: "pass", evidence: "All buckets enforce SSL requests." },
    { control_id: "CIS-S3-2.2", control_status: "pass", evidence: "Public access block enabled on all buckets." },
    { control_id: "CIS-EC2-3.1", control_status: "fail", evidence: "Two EBS volumes unencrypted." },
    { control_id: "CIS-EC2-3.2", control_status: "pass", evidence: "No unrestricted SSH ingress." },
    { control_id: "CIS-CLOUDTRAIL-4.1", control_status: "pass", evidence: "CloudTrail enabled in all regions." },
    { control_id: "CIS-VPC-6.1", control_status: "na", evidence: "No peering connections configured." },
    { control_id: "CIS-VPC-6.2", control_status: "pass", evidence: "VPC flow logs enabled." },
  ],
  "app-003": [
    { control_id: "CIS-IAM-1.1", control_status: "fail", evidence: "MFA not enabled on root account." },
    { control_id: "CIS-S3-2.1", control_status: "pass", evidence: "All buckets enforce SSL requests." },
    { control_id: "CIS-S3-2.2", control_status: "pass", evidence: "Public access block enabled." },
    { control_id: "CIS-EC2-3.1", control_status: "pass", evidence: "EBS volumes encrypted at rest." },
    { control_id: "CIS-CLOUDTRAIL-4.1", control_status: "na", evidence: "Centralized logging managed in separate account." },
  ],
};

const DUMMY_SCAN_INFO: Record<string, ScanInfo> = {
  "app-001": { lastScanTime: "2026-06-18T14:32:00Z", catalogName: "CIS AWS Foundations v3.0", accountId: "1234-5678-9012", sspName: "Payments Platform" },
  "app-002": { lastScanTime: "2026-06-17T09:15:00Z", catalogName: "CIS AWS Foundations v3.0", accountId: "2345-6789-0123", sspName: "Customer Portal" },
  "app-003": { lastScanTime: "2026-06-16T22:48:00Z", catalogName: "CIS AWS Foundations v3.0", accountId: "3456-7890-1234", sspName: "Analytics Pipeline" },
};

function buildSummary(controls: ControlItem[]): ArchAssetSummary {
  const pass = controls.filter(c => c.control_status === "pass").length;
  const fail = controls.filter(c => c.control_status === "fail").length;
  const na   = controls.filter(c => c.control_status === "na").length;
  const total = controls.length;
  return { pass, fail, na, total, passRate: total > 0 ? Math.round((pass / total) * 100) : 0 };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
function svcOf(id: string) { return id.split("-")[1] ?? "unknown"; }
function fmtDate(iso: string) {
  return new Date(iso).toLocaleString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}
function groupByService(items: ControlItem[]): ServiceGroup[] {
  const map = new Map<string, ServiceGroup>();
  for (const c of items) {
    const svc = svcOf(c.control_id);
    if (!map.has(svc)) map.set(svc, { service: svc, pass: 0, fail: 0, na: 0, total: 0, controls: [] });
    const g = map.get(svc)!;
    g[c.control_status]++;
    g.total++;
    g.controls.push(c);
  }
  return [...map.values()].sort((a, b) => b.fail - a.fail || a.service.localeCompare(b.service));
}
function scoreColor(score: number) { return score >= 80 ? "#22c55e" : score >= 60 ? "#f97316" : "#ef4444"; }

// ─── Atoms ───────────────────────────────────────────────────────────────────
function SIcon({ s, sz = 16 }: { s: ControlStatus; sz?: number }) {
  const st = { width: sz, height: sz };
  if (s === "pass") return <CheckCircle2 style={st} className="text-emerald-500 dark:text-emerald-400 shrink-0" />;
  if (s === "fail") return <XCircle style={st} className="text-red-500 dark:text-red-400 shrink-0" />;
  return <MinusCircle style={st} className="text-slate-400 dark:text-gray-400 shrink-0" />;
}

function SBadge({ status }: { status: ControlStatus }) {
  const cls = {
    pass: "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/30",
    fail: "bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 border-red-200 dark:border-red-500/30",
    na:   "bg-slate-100 dark:bg-gray-600/20 text-slate-500 dark:text-gray-300 border-slate-200 dark:border-gray-500/30",
  };
  const lbl = status === "pass" ? "PASS" : status === "fail" ? "FAIL" : "N/A";
  return <span className={`px-2 py-0.5 rounded text-xs font-bold border tracking-wide ${cls[status]}`}>{lbl}</span>;
}

function RBadge({ fail }: { fail: number }) {
  if (fail >= 4) return <span className="px-2 py-0.5 rounded text-xs font-bold bg-red-50 dark:bg-red-500/15 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-500/30">High Risk</span>;
  if (fail >= 2) return <span className="px-2 py-0.5 rounded text-xs font-bold bg-orange-50 dark:bg-orange-500/15 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-500/30">Med Risk</span>;
  if (fail === 1) return <span className="px-2 py-0.5 rounded text-xs font-bold bg-yellow-50 dark:bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-500/30">Low Risk</span>;
  return <span className="px-2 py-0.5 rounded text-xs font-bold bg-emerald-50 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30">Compliant</span>;
}

function Bar({ pct, color, h = "h-2" }: { pct: number; color: string; h?: string }) {
  return (
    <div className={`flex-1 ${h} rounded-full overflow-hidden bg-black/[0.06] dark:bg-white/[0.07]`}>
      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
    </div>
  );
}

// ─── Section 1 (Left): Services → Controls drilldown ──────────────────────────
function LeftServiceOverview({ groups, summary, scanInfo, totalControls, expanded, onToggleSvc, selCtrl, onCtrl, hasSelection }: {
  groups: ServiceGroup[];
  summary: ArchAssetSummary;
  scanInfo: ScanInfo;
  totalControls: number;
  expanded: Set<string>;
  onToggleSvc: (s: string) => void;
  selCtrl: string | null;
  onCtrl: (id: string) => void;
  hasSelection: boolean;
}) {
  const { theme } = useTheme();
  const dark = theme !== "light";
  const [search, setSearch] = useState("");

  const borderClr = dark ? "rgba(255,255,255,0.08)" : "#e2e8f0";
  const panelBg   = dark ? "#05070e" : "#ffffff";
  const cardBg    = dark ? "rgba(255,255,255,0.03)" : "#f8fafc";
  const cardBd    = dark ? "rgba(255,255,255,0.09)" : "#e2e8f0";
  const rowBd     = dark ? "rgba(255,255,255,0.06)" : "#eef2f7";
  const inputBg   = dark ? "rgba(255,255,255,0.04)" : "#ffffff";
  const inputBd   = dark ? "rgba(255,255,255,0.12)" : "#e2e8f0";

  const score  = summary.total > 0 ? Math.round((summary.pass / summary.total) * 100) : 0;
  const sColor = scoreColor(score);

  // Filter services by name or by any control id within the service.
  const q = search.trim().toLowerCase();
  const visibleGroups = q
    ? groups.filter(
        (g) =>
          g.service.toLowerCase().includes(q) ||
          g.controls.some((c) => c.control_id.toLowerCase().includes(q)),
      )
    : groups;

  return (
    <div
      className={`${hasSelection ? "w-[42%]" : "flex-1"} shrink-0 flex flex-col h-full border-r overflow-y-auto`}
      style={{ background: panelBg, borderColor: borderClr }}
    >
      {/* Hero card */}
      <div className="p-4 border-b" style={{ borderColor: borderClr }}>
        <div className="rounded-2xl p-4" style={{ background: cardBg, border: `1px solid ${cardBd}` }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 rounded-xl shrink-0" style={{ background: "rgba(59,130,246,0.15)", border: "1px solid rgba(59,130,246,0.28)" }}>
              <Shield className="w-5 h-5 text-blue-400" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-base font-bold text-[#0f172a] dark:text-white">{scanInfo.catalogName || "CIS AWS Compliance"}</h1>
              <p className="text-xs text-[#64748b] dark:text-gray-300 mt-0.5">
                {scanInfo.lastScanTime ? `Scanned ${fmtDate(scanInfo.lastScanTime)}` : "—"}
              </p>
            </div>
            <div className="text-right shrink-0">
              <div className="text-3xl font-black leading-none" style={{ color: sColor }}>
                {score}<span className="text-lg">%</span>
              </div>
              <div className="text-xs font-semibold mt-0.5" style={{ color: sColor }}>
                {score >= 80 ? "Good Standing" : score >= 60 ? "Needs Attention" : "Critical Issues"}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {[
              { label: "Passed", val: summary.pass,
                cls: dark ? "text-emerald-400" : "text-emerald-700",
                bg:  dark ? "rgba(34,197,94,0.08)"   : "#f0fdf4",
                bd:  dark ? "rgba(34,197,94,0.2)"    : "#bbf7d0" },
              { label: "Failed", val: summary.fail,
                cls: dark ? "text-red-400"    : "text-red-600",
                bg:  dark ? "rgba(239,68,68,0.08)"   : "#fef2f2",
                bd:  dark ? "rgba(239,68,68,0.2)"    : "#fecaca" },
              { label: "N/A",    val: summary.na,
                cls: dark ? "text-gray-300"   : "text-slate-500",
                bg:  dark ? "rgba(107,114,128,0.06)" : "#f8fafc",
                bd:  dark ? "rgba(107,114,128,0.18)" : "#e2e8f0" },
            ].map(({ label, val, cls, bg, bd }) => (
              <div key={label} className="flex-1 rounded-xl px-2 py-2 flex items-center gap-1.5" style={{ background: bg, border: `1px solid ${bd}` }}>
                <div className={`text-xl font-black ${cls}`}>{val}</div>
                <div className={`text-xs ${dark ? "text-white" : "text-[#0f172a]"}`}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="px-4 pt-3 pb-1">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search services or controls"
            maxLength={100}
            className="w-full rounded-xl pl-9 pr-9 py-2 text-sm text-[#0f172a] dark:text-white placeholder:text-[#94a3b8] dark:placeholder:text-gray-500 outline-none focus:border-blue-400 transition-colors"
            style={{ background: inputBg, border: `1px solid ${inputBd}` }}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded-md text-slate-400 dark:text-gray-400 hover:text-slate-600 dark:hover:text-gray-200"
              title="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Services label */}
      <div className="px-4 pt-2 pb-2 flex items-center justify-between">
        <span className="text-xs font-bold text-[#94a3b8] dark:text-gray-400 tracking-widest">Services ({visibleGroups.length})</span>
        <span className="text-xs text-[#94a3b8] dark:text-gray-400">{totalControls} controls</span>
      </div>

      {/* Services + Controls drilldown */}
      <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-2">
        {visibleGroups.length === 0 && (
          <div className="px-3 py-8 text-center text-xs text-[#94a3b8] dark:text-gray-400">
            No services match "{search}"
          </div>
        )}
        {visibleGroups.map((g) => {
          const pct       = g.total > 0 ? Math.round(((g.pass + g.na) / g.total) * 100) : 0;
          const barColor  = g.fail > 0 ? "#ef4444" : "#22c55e";
          const accentBd  = g.fail >= 4 ? "rgba(239,68,68,0.3)" : g.fail > 0 ? "rgba(249,115,22,0.25)" : "rgba(34,197,94,0.25)";
          const isOpen    = expanded.has(g.service);
          const defaultBg = dark ? "rgba(255,255,255,0.03)" : "#ffffff";
          const hoverBg   = dark ? "rgba(255,255,255,0.06)" : "#f8fafc";
          return (
            <div key={g.service} className="rounded-xl overflow-hidden" style={{ border: `1px solid ${accentBd}`, background: defaultBg }}>
              {/* Service header — toggles the controls drilldown */}
              <button
                onClick={() => onToggleSvc(g.service)}
                className="w-full text-left p-3 transition-colors"
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = hoverBg; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <ChevronDown className={`w-4 h-4 shrink-0 text-slate-400 dark:text-gray-400 transition-transform ${isOpen ? "" : "-rotate-90"}`} />
                  <div className="text-sm font-bold text-[#0f172a] dark:text-white leading-tight flex-1 min-w-0">{g.service}</div>
                  <RBadge fail={g.fail} />
                </div>
                <div className="pl-6">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-[#64748b] dark:text-gray-400">{g.total} controls</span>
                    <span className="font-bold" style={{ color: barColor }}>{pct}%</span>
                  </div>
                  <Bar pct={pct} color={barColor} h="h-1.5" />
                  <div className="flex items-center gap-2 text-xs mt-2">
                    <span className="text-emerald-600 dark:text-emerald-400 font-bold">{g.pass}P</span>
                    <span className="text-red-500 dark:text-red-400 font-bold">{g.fail}F</span>
                    <span className="text-slate-400 dark:text-gray-400">{g.na}N/A</span>
                    {g.fail > 0 && (
                      <div className="ml-auto flex items-center gap-0.5 text-orange-500 dark:text-orange-400">
                        <AlertTriangle className="w-3 h-3" />
                        <span>{g.fail} open</span>
                      </div>
                    )}
                  </div>
                </div>
              </button>

              {/* Controls drilldown */}
              {isOpen && (
                <div style={{ borderTop: `1px solid ${rowBd}` }}>
                  {g.controls.map((c, i) => {
                    const isSel  = selCtrl === c.control_id;
                    const cHover = dark ? "rgba(255,255,255,0.05)" : "#f1f5f9";
                    const cSel   = dark ? "rgba(59,130,246,0.14)"  : "rgba(59,130,246,0.08)";
                    return (
                      <button
                        key={c.control_id}
                        onClick={() => onCtrl(c.control_id)}
                        className="w-full flex items-center gap-2.5 pl-6 pr-3 py-2.5 text-left transition-colors"
                        style={{
                          borderTop: i > 0 ? `1px solid ${rowBd}` : "none",
                          background: isSel ? cSel : "transparent",
                        }}
                        onMouseEnter={e => { if (!isSel) (e.currentTarget as HTMLElement).style.background = cHover; }}
                        onMouseLeave={e => { if (!isSel) (e.currentTarget as HTMLElement).style.background = "transparent"; }}
                      >
                        <SIcon s={c.control_status} sz={15} />
                        <span className="font-mono text-xs text-[#0f172a] dark:text-white flex-1 min-w-0">{c.control_id?.toUpperCase()}</span>
                        <SBadge status={c.control_status} />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Panel 2 (Middle): Control Detail ────────────────────────────────────────
function ControlDetail({ ctrl, group, onBack }: {
  ctrl: ControlItem;
  group: ServiceGroup;
  onBack: () => void;
}) {
  const { theme } = useTheme();
  const dark = theme !== "light";

  const borderClr  = dark ? "rgba(255,255,255,0.08)" : "#e2e8f0";
  const rowBd      = dark ? "rgba(255,255,255,0.06)" : "#f1f5f9";
  const cardBg     = dark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const cardBd     = dark ? "rgba(255,255,255,0.08)" : "#e2e8f0";
  const btnBg      = dark ? "rgba(255,255,255,0.05)" : "#f8fafc";
  const btnBd      = dark ? "rgba(255,255,255,0.1)"  : "#e2e8f0";
  const btnHoverBg = dark ? "rgba(255,255,255,0.1)"  : "#f1f5f9";

  const st     = ctrl.control_status;
  const label  = ctrl.control_id;
  const svc    = svcOf(ctrl.control_id);
  const ver    = ctrl.control_id.split("-").slice(2).join("-");

  const iconBg = st === "pass"
    ? (dark ? "rgba(34,197,94,0.12)"   : "#f0fdf4")
    : st === "fail"
      ? (dark ? "rgba(239,68,68,0.12)"   : "#fef2f2")
      : (dark ? "rgba(107,114,128,0.12)" : "#f8fafc");
  const iconBd = st === "pass"
    ? (dark ? "rgba(34,197,94,0.3)"    : "#bbf7d0")
    : st === "fail"
      ? (dark ? "rgba(239,68,68,0.3)"    : "#fecaca")
      : (dark ? "rgba(107,114,128,0.25)" : "#e2e8f0");

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-6 border-b" style={{ borderColor: borderClr }}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-1.5 rounded-lg transition-colors shrink-0"
              style={{ background: btnBg, border: `1px solid ${btnBd}` }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = btnHoverBg; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = btnBg; }}
              title="Back to controls"
            >
              <ArrowLeft className="w-4 h-4 text-slate-500 dark:text-gray-300" />
            </button>
            <div className="p-3 rounded-2xl" style={{ background: iconBg, border: `1px solid ${iconBd}` }}>
              <SIcon s={st} sz={26} />
            </div>
            <div>
              <div className="text-xl font-black text-[#0f172a] dark:text-white font-mono">{ctrl.control_id?.toUpperCase()}</div>
              <div className="text-sm text-[#0f172a] dark:text-white mt-1">{label}</div>
              <div className="text-xs text-[#64748b] dark:text-gray-300 mt-0.5">{svc} · CIS AWS Benchmarks</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <RBadge fail={group.fail} />
            <SBadge status={st} />
          </div>
        </div>
      </div>

      <div className="p-6 space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs font-bold text-[#94a3b8] dark:text-gray-400 tracking-widest mb-3">Compliance Mapping</div>
            <div className="rounded-xl p-4" style={{ background: cardBg, border: `1px solid ${cardBd}` }}>
              <div className="text-sm font-bold text-blue-600 dark:text-blue-400 mb-3">CIS AWS Benchmarks</div>
              <ul className="space-y-2.5">
                {[
                  svc,
                  `Control ${ver}: ${label}`,
                  "Combined OSCAL Catalog",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-[#0f172a] dark:text-white">
                    <span className="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5" style={{ background: "#60a5fa" }} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div>
            <div className="text-xs font-bold text-[#94a3b8] dark:text-gray-400 tracking-widest mb-3">Risk Assessment</div>
            <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${cardBd}` }}>
              {[
                { label: "Control Status",
                  value: st === "pass" ? "Passed" : st === "fail" ? "Failed" : "Not Applicable",
                  cls: st === "pass" ? "text-emerald-600 dark:text-emerald-400" : st === "fail" ? "text-red-600 dark:text-red-400" : "text-slate-500 dark:text-gray-300" },
                { label: "Service Risk",
                  value: group.fail >= 4 ? "High" : group.fail >= 2 ? "Medium" : group.fail === 1 ? "Low" : "Compliant",
                  cls: group.fail >= 4 ? "text-red-600 dark:text-red-400" : group.fail >= 2 ? "text-orange-600 dark:text-orange-400" : group.fail === 1 ? "text-yellow-600 dark:text-yellow-400" : "text-emerald-600 dark:text-emerald-400" },
                { label: "Open Findings",
                  value: String(group.fail),
                  cls: group.fail > 0 ? "text-red-600 dark:text-red-400" : "text-emerald-600 dark:text-emerald-400" },
              ].map(({ label, value, cls }, i) => (
                <div key={label} className="flex justify-between items-center px-4 py-3.5"
                  style={{ borderTop: i > 0 ? `1px solid ${rowBd}` : "none" }}>
                  <span className="text-sm text-[#0f172a] dark:text-white">{label}</span>
                  <span className={`text-sm font-bold ${cls}`}>{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <ControlExtras status={st} evidence={ctrl.evidence} service={svc} />
      </div>
    </div>
  );
}

// ─── Control detail extras (fills the detail panel below the top grid) ─────────
const REMEDIATION: Record<string, string> = {
  IAM: "Enforce least-privilege IAM policies, enable MFA for all users, and rotate access keys older than 90 days.",
  S3: "Enable S3 Block Public Access at the account level, enforce SSL-only bucket policies, and turn on default encryption.",
  EC2: "Restrict security group ingress to known CIDR ranges, close public SSH/RDP, and apply the latest patch baseline.",
  CLOUDTRAIL: "Enable multi-region CloudTrail with log file validation and ship logs to a write-once S3 bucket.",
  RDS: "Disable public accessibility, enable encryption at rest, and verify automated backups with point-in-time recovery.",
  VPC: "Enable VPC flow logs, remove unused peering routes, and segment subnets by trust boundary.",
};

function ControlExtras({ status, evidence, service }: { status: ControlStatus; evidence?: string; service: string }) {
  const { theme } = useTheme();
  const dark = theme !== "light";

  const rowBd  = dark ? "rgba(255,255,255,0.06)" : "#f1f5f9";
  const cardBg = dark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const cardBd = dark ? "rgba(255,255,255,0.08)" : "#e2e8f0";

  const ok   = status === "pass";
  const tone = ok ? "#22c55e" : status === "fail" ? "#ef4444" : "#94a3b8";

  // Resilience posture varies with the control outcome so the panel reflects status.
  const resilience: { label: string; value: string; good: boolean }[] = [
    { label: "Backup Enabled",      value: "Yes",            good: true },
    { label: "Backup Success Rate", value: ok ? "99%" : "94%", good: ok },
    { label: "Restore Tested",      value: ok ? "Yes" : "No", good: ok },
    { label: "Multi-AZ",            value: ok ? "Yes" : "Partial", good: ok },
    { label: "DR Coverage",         value: ok ? "Full" : "Partial", good: ok },
    { label: "RPO",                 value: "15 mins",        good: true },
    { label: "RTO",                 value: ok ? "1 hr" : "2 hrs", good: ok },
  ];

  const Label = ({ children }: { children: React.ReactNode }) => (
    <div className="text-xs font-bold text-[#94a3b8] dark:text-gray-400 tracking-widest mb-3">{children}</div>
  );

  return (
    <>
      {/* Evidence */}
      <div>
        <Label>Evidence</Label>
        <div className="rounded-xl p-4" style={{ background: cardBg, border: `1px solid ${cardBd}` }}>
          <div className="flex items-start gap-3">
            <span className="w-2 h-2 rounded-full shrink-0 mt-1.5" style={{ background: tone }} />
            <p className="text-sm text-[#0f172a] dark:text-white leading-relaxed">
              {evidence || "No evidence was captured for this control during the last scan."}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Resilience */}
        <div>
          <Label>Resilience</Label>
          <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${cardBd}` }}>
            {resilience.map((r, i) => (
              <div key={r.label} className="flex justify-between items-center px-4 py-3"
                style={{ borderTop: i > 0 ? `1px solid ${rowBd}` : "none" }}>
                <span className="text-sm text-[#0f172a] dark:text-white">{r.label}</span>
                <span className={`text-sm font-bold ${r.good ? "text-emerald-600 dark:text-emerald-400" : "text-orange-600 dark:text-orange-400"}`}>{r.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Remediation */}
        <div>
          <Label>{ok ? "Status" : "Remediation"}</Label>
          <div className="rounded-xl p-4 h-full" style={{ background: cardBg, border: `1px solid ${cardBd}` }}>
            {ok ? (
              <p className="text-sm text-emerald-600 dark:text-emerald-400 leading-relaxed">
                This control passed its last evaluation. No remediation is required — continue monitoring on the next scan cycle.
              </p>
            ) : (
              <p className="text-sm text-[#0f172a] dark:text-white leading-relaxed">
                {REMEDIATION[service.toUpperCase()] ?? "Review the failing configuration against the CIS AWS benchmark and apply the recommended hardening guidance."}
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Account summary dummy data + Panel 3 (Right): Account-level rollup ────────
interface FrameworkScore { label: string; pct: number; }
interface ResilienceMetric { label: string; value: string; pct?: number; }
interface AccountSummary {
  components: number; assets: number; totalControls: number; passed: number; failed: number; complianceScore: number;
  im8: FrameworkScore[]; im8Overall: number; trmg: FrameworkScore[]; trmgOverall: number;
  risk: { critical: number; high: number; medium: number; low: number }; resilience: ResilienceMetric[];
}

const DUMMY_ACCOUNT_SUMMARY: Record<string, AccountSummary> = {
  "app-001": {
    components: 15, assets: 420, totalControls: 18000, passed: 16920, failed: 1080, complianceScore: 94,
    im8: [
      { label: "Container Security", pct: 95 },
      { label: "Infrastructure Security", pct: 92 },
      { label: "Secure Development", pct: 97 },
      { label: "Resilience", pct: 88 },
    ],
    im8Overall: 93,
    trmg: [
      { label: "Access Control", pct: 96 },
      { label: "Vulnerability Management", pct: 91 },
      { label: "Data Protection", pct: 94 },
      { label: "Recovery Management", pct: 87 },
    ],
    trmgOverall: 92,
    risk: { critical: 5, high: 12, medium: 35, low: 78 },
    resilience: [
      { label: "Backup Coverage", value: "98%", pct: 98 },
      { label: "DR Coverage", value: "90%", pct: 90 },
      { label: "Assets with Tested Recovery", value: "85%", pct: 85 },
      { label: "Average RPO", value: "15 mins" },
      { label: "Average RTO", value: "2 hrs" },
    ],
  },
  "app-002": {
    components: 9, assets: 260, totalControls: 11400, passed: 10830, failed: 570, complianceScore: 95,
    im8: [
      { label: "Container Security", pct: 97 },
      { label: "Infrastructure Security", pct: 94 },
      { label: "Secure Development", pct: 96 },
      { label: "Resilience", pct: 90 },
    ],
    im8Overall: 94,
    trmg: [
      { label: "Access Control", pct: 98 },
      { label: "Vulnerability Management", pct: 93 },
      { label: "Data Protection", pct: 95 },
      { label: "Recovery Management", pct: 89 },
    ],
    trmgOverall: 94,
    risk: { critical: 2, high: 7, medium: 21, low: 64 },
    resilience: [
      { label: "Backup Coverage", value: "99%", pct: 99 },
      { label: "DR Coverage", value: "92%", pct: 92 },
      { label: "Assets with Tested Recovery", value: "88%", pct: 88 },
      { label: "Average RPO", value: "10 mins" },
      { label: "Average RTO", value: "90 mins" },
    ],
  },
  "app-003": {
    components: 5, assets: 130, totalControls: 5600, passed: 5096, failed: 504, complianceScore: 91,
    im8: [
      { label: "Container Security", pct: 90 },
      { label: "Infrastructure Security", pct: 89 },
      { label: "Secure Development", pct: 93 },
      { label: "Resilience", pct: 85 },
    ],
    im8Overall: 89,
    trmg: [
      { label: "Access Control", pct: 92 },
      { label: "Vulnerability Management", pct: 88 },
      { label: "Data Protection", pct: 90 },
      { label: "Recovery Management", pct: 84 },
    ],
    trmgOverall: 89,
    risk: { critical: 8, high: 18, medium: 41, low: 52 },
    resilience: [
      { label: "Backup Coverage", value: "95%", pct: 95 },
      { label: "DR Coverage", value: "86%", pct: 86 },
      { label: "Assets with Tested Recovery", value: "80%", pct: 80 },
      { label: "Average RPO", value: "30 mins" },
      { label: "Average RTO", value: "4 hrs" },
    ],
  },
};

function summaryBarColor(pct: number) { return pct >= 90 ? "#22c55e" : pct >= 80 ? "#f97316" : "#ef4444"; }

function AccountSummaryPanel({ appId }: { appId: string | null }) {
  const { theme } = useTheme();
  const dark = theme !== "light";

  const data = (appId && DUMMY_ACCOUNT_SUMMARY[appId]) || null;
  if (!data) return null;

  const borderClr = dark ? "rgba(255,255,255,0.08)" : "#e2e8f0";
  const panelBg   = dark ? "#05070e" : "#ffffff";
  const cardBg    = dark ? "rgba(255,255,255,0.03)" : "#f8fafc";
  const cardBd    = dark ? "rgba(255,255,255,0.09)" : "#e2e8f0";
  const rowBd     = dark ? "rgba(255,255,255,0.06)" : "#eef2f7";

  const Card = ({ children }: { children: React.ReactNode }) => (
    <div className="rounded-2xl p-4" style={{ background: cardBg, border: `1px solid ${cardBd}` }}>{children}</div>
  );
  const Heading = ({ children, color }: { children: React.ReactNode; color?: string }) => (
    <div className="text-sm font-bold mb-3" style={{ color: color ?? (dark ? "#ffffff" : "#0f172a") }}>{children}</div>
  );
  const SBar = ({ pct }: { pct: number }) => (
    <div className="flex-1 h-1.5 rounded-full overflow-hidden bg-black/[0.06] dark:bg-white/[0.07]">
      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: summaryBarColor(pct) }} />
    </div>
  );
  const FrameworkRows = ({ rows, overall, overallLabel }: { rows: FrameworkScore[]; overall: number; overallLabel: string }) => (
    <div className="space-y-2.5">
      {rows.map((r) => (
        <div key={r.label} className="flex items-center gap-3">
          <span className="text-xs text-[#0f172a] dark:text-white flex-1 min-w-0">{r.label}</span>
          <div className="w-24"><SBar pct={r.pct} /></div>
          <span className="text-xs font-bold w-9 text-right" style={{ color: summaryBarColor(r.pct) }}>{r.pct}%</span>
        </div>
      ))}
      <div className="flex items-center gap-3 pt-1" style={{ borderTop: `1px solid ${rowBd}` }}>
        <span className="text-xs font-bold flex-1 min-w-0 text-blue-600 dark:text-blue-400 mt-2">{overallLabel}</span>
        <div className="w-24 mt-2"><SBar pct={overall} /></div>
        <span className="text-xs font-bold w-9 text-right mt-2 text-blue-600 dark:text-blue-400">{overall}%</span>
      </div>
    </div>
  );

  const riskCells = [
    { label: "Critical", val: data.risk.critical, cls: "text-red-600 dark:text-red-400",      bg: dark ? "rgba(239,68,68,0.08)"  : "#fef2f2", bd: dark ? "rgba(239,68,68,0.2)"  : "#fecaca" },
    { label: "High",     val: data.risk.high,     cls: "text-orange-600 dark:text-orange-400", bg: dark ? "rgba(249,115,22,0.08)" : "#fff7ed", bd: dark ? "rgba(249,115,22,0.2)" : "#fed7aa" },
    { label: "Medium",   val: data.risk.medium,   cls: "text-yellow-600 dark:text-yellow-400", bg: dark ? "rgba(234,179,8,0.08)"  : "#fefce8", bd: dark ? "rgba(234,179,8,0.2)"  : "#fef08a" },
    { label: "Low",      val: data.risk.low,      cls: "text-emerald-600 dark:text-emerald-400", bg: dark ? "rgba(34,197,94,0.08)" : "#f0fdf4", bd: dark ? "rgba(34,197,94,0.2)"  : "#bbf7d0" },
  ];

  return (
    <div
      className="hidden xl:flex w-[340px] shrink-0 flex-col h-full border-l overflow-y-auto"
      style={{ background: panelBg, borderColor: borderClr }}
    >
      <div className="p-4 space-y-3">
        <div className="text-base font-bold text-[#0f172a] dark:text-white">Account Summary</div>

        {/* Components / Assets */}
        <div className="grid grid-cols-2 gap-3">
          {[{ label: "Components", val: data.components }, { label: "Assets", val: data.assets }].map((s) => (
            <div key={s.label} className="rounded-2xl p-4 text-center" style={{ background: cardBg, border: `1px solid ${cardBd}` }}>
              <div className="text-xs text-[#64748b] dark:text-gray-400">{s.label}</div>
              <div className="text-2xl font-black text-[#0f172a] dark:text-white mt-1">{s.val.toLocaleString()}</div>
            </div>
          ))}
        </div>

        {/* Controls */}
        <Card>
          <Heading>Controls</Heading>
          {[
            { label: "Total Controls Evaluated", value: data.totalControls.toLocaleString(), cls: "text-[#0f172a] dark:text-white" },
            { label: "Passed", value: data.passed.toLocaleString(), cls: "text-emerald-600 dark:text-emerald-400" },
            { label: "Failed", value: data.failed.toLocaleString(), cls: "text-red-600 dark:text-red-400" },
            { label: "Compliance Score", value: `${data.complianceScore} %`, cls: "text-emerald-600 dark:text-emerald-400" },
          ].map((r, i) => (
            <div key={r.label} className="flex items-center justify-between py-2" style={{ borderTop: i > 0 ? `1px solid ${rowBd}` : "none" }}>
              <span className="text-xs text-[#64748b] dark:text-gray-400">{r.label}</span>
              <span className={`text-sm font-bold ${r.cls}`}>{r.value}</span>
            </div>
          ))}
        </Card>

        {/* IM8 */}
        <Card>
          <Heading color={dark ? "#60a5fa" : "#2563eb"}>IM8</Heading>
          <FrameworkRows rows={data.im8} overall={data.im8Overall} overallLabel="Overall IM8" />
        </Card>

        {/* TRMG */}
        <Card>
          <Heading color={dark ? "#60a5fa" : "#2563eb"}>TRMG</Heading>
          <FrameworkRows rows={data.trmg} overall={data.trmgOverall} overallLabel="Overall TRMG" />
        </Card>

        {/* Risk */}
        <Card>
          <Heading>Risk</Heading>
          <div className="grid grid-cols-4 gap-2">
            {riskCells.map((c) => (
              <div key={c.label} className="rounded-xl py-2 text-center" style={{ background: c.bg, border: `1px solid ${c.bd}` }}>
                <div className="text-[10px] font-semibold text-[#64748b] dark:text-gray-400">{c.label}</div>
                <div className={`text-lg font-black ${c.cls}`}>{c.val}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* Resilience */}
        <Card>
          <Heading>Resilience</Heading>
          <div className="space-y-2.5">
            {data.resilience.map((m) => (
              <div key={m.label} className="flex items-center gap-3">
                <span className="text-xs text-[#0f172a] dark:text-white flex-1 min-w-0">{m.label}</span>
                {m.pct != null && <div className="w-20"><SBar pct={m.pct} /></div>}
                <span className="text-xs font-bold w-14 text-right text-[#0f172a] dark:text-white">{m.value}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

// ─── Application Dropdown (top-right) ─────────────────────────────────────────
function AppDropdown({ apps, value, onChange, loading }: {
  apps: ArchApplicationListItem[];
  value: string | null;
  onChange: (id: string) => void;
  loading: boolean;
}) {
  const { theme } = useTheme();
  const dark = theme !== "light";
  const [open, setOpen] = useState(false);

  const btnBg   = dark ? "rgba(255,255,255,0.04)" : "#ffffff";  
  const btnBd   = dark ? "rgba(255,255,255,0.12)" : "#e2e8f0";
  const menuBg  = dark ? "#0b0f17" : "#ffffff";
  const menuBd  = dark ? "rgba(255,255,255,0.12)" : "#e2e8f0";
  const hoverBg = dark ? "rgba(255,255,255,0.06)" : "#f1f5f9";
  const selBg   = dark ? "rgba(59,130,246,0.14)" : "rgba(59,130,246,0.08)";

  const selected = apps.find((a) => a.applicationId === value) ?? null;

  return (
    <div className="relative shrink-0">
      <button
        type="button"
        disabled={loading || apps.length === 0}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-60"
        style={{ background: btnBg, border: `1px solid ${btnBd}`, minWidth: 200 }}
      >
        <span className="flex-1 text-left text-[#0f172a] dark:text-white">
          {loading ? "Loading applications…" : selected?.applicationName ?? "Select application"}
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-400 dark:text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 mt-2 z-20 w-72 max-h-80 overflow-y-auto rounded-xl py-1 shadow-lg"
            style={{ background: menuBg, border: `1px solid ${menuBd}` }}
          >
            {apps.map((a) => {
              const isSel = a.applicationId === value;
              const subtitle = a.description ?? `${a.cloudAccountCount} cloud account${a.cloudAccountCount === 1 ? "" : "s"}`;
              return (
                <button
                  key={a.applicationId}
                  type="button"
                  onClick={() => { onChange(a.applicationId); setOpen(false); }}
                  className="w-full text-left px-3 py-2 text-sm transition-colors text-[#0f172a] dark:text-white"
                  style={{ background: isSel ? selBg : "transparent" }}
                  onMouseEnter={(e) => { if (!isSel) (e.currentTarget as HTMLElement).style.background = hoverBg; }}
                  onMouseLeave={(e) => { if (!isSel) (e.currentTarget as HTMLElement).style.background = "transparent"; }}
                >
                  <div className="font-semibold leading-tight">{a.applicationName}</div>
                  {subtitle && (
                    <div className="text-xs text-[#64748b] dark:text-gray-400 mt-0.5">{subtitle}</div>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export function ArchitectureCompliance() {
  const [apps, setApps]         = useState<ArchApplicationListItem[]>([]);
  const [appsLoading, setAppsLoading] = useState(true);
  const [selAppId, setSelAppId] = useState<string | null>(null);
  const [controls, setControls] = useState<ControlItem[]>([]);
  const [summary, setSummary]   = useState<ArchAssetSummary>({ pass: 0, fail: 0, na: 0, total: 0, passRate: 0 });
  const [scanInfo, setScanInfo] = useState<ScanInfo>({ lastScanTime: "", catalogName: "", accountId: "", sspName: "" });
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [selCtrl, setSelCtrl]   = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const { theme } = useTheme();
  const dark = theme !== "light";

  const groups = useMemo(() => groupByService(controls), [controls]);

  // Load the dummy application list for the dropdown (no API).
  useEffect(() => {
    setApps(DUMMY_APPS);
    if (DUMMY_APPS.length) setSelAppId(DUMMY_APPS[0].applicationId);
    else setError("No applications have cloud accounts linked yet.");
    setAppsLoading(false);
  }, []);

  // Load dummy compliance data for the selected application (no API).
  useEffect(() => {
    if (!selAppId) return;
    setLoading(true);
    setError(null);

    const dummyControls = DUMMY_CONTROLS[selAppId] ?? [];
    const dummyScan = DUMMY_SCAN_INFO[selAppId] ?? { lastScanTime: "", catalogName: "", accountId: "", sspName: "" };

    // Auto-select the first service's first control so the detail panel is populated.
    const grps = groupByService(dummyControls);
    const firstCtrl = grps[0]?.controls[0]?.control_id ?? null;

    setScanInfo(dummyScan);
    setControls(dummyControls);
    setSummary(buildSummary(dummyControls));
    setSelCtrl(firstCtrl);
    setExpanded(firstCtrl ? new Set([svcOf(firstCtrl)]) : new Set());
    setLoading(false);
  }, [selAppId]);

  // Selecting a control opens its service drilldown and shows its detail in section 2.
  const handleCtrl = (id: string) => {
    setSelCtrl(id);
    setExpanded(prev => new Set(prev).add(svcOf(id)));
  };
  const handleToggleSvc = (svc: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(svc)) next.delete(svc);
      else next.add(svc);
      return next;
    });
  };

  const ctrl = selCtrl ? controls.find(c => c.control_id === selCtrl) ?? null : null;
  const grp  = selCtrl ? groups.find(g => g.service === svcOf(selCtrl)) ?? null : null;

  const mainBg    = dark ? "#07090e" : "#f5f7fb";
  const panelBg   = dark ? "#05070e" : "#ffffff";
  const borderClr = dark ? "rgba(255,255,255,0.08)" : "#e2e8f0";

  // Body keeps the previous content visible while new data loads (no blank pages).
  let body: React.ReactNode;
  if (appsLoading) {
    body = (
      <div className="flex flex-1 min-h-0 items-center justify-center" style={{ background: mainBg }}>
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
          <span className="text-sm text-[#64748b] dark:text-gray-400">Loading applications…</span>
        </div>
      </div>
    );
  } else if (error && controls.length === 0) {
    body = (
      <div className="flex flex-1 min-h-0 items-center justify-center" style={{ background: mainBg }}>
        <div className="text-center">
          <AlertTriangle className="w-10 h-10 text-orange-400 mx-auto mb-3" />
          <p className="text-sm font-semibold text-[#0f172a] dark:text-white mb-1">Failed to load</p>
          <p className="text-xs text-[#64748b] dark:text-gray-400">{error}</p>
        </div>
      </div>
    );
  } else {
    body = (
      <div className="flex flex-1 min-h-0 overflow-hidden" style={{ background: mainBg }}>
        {/* Section 1: Services → Controls drilldown */}
        <LeftServiceOverview
          groups={groups}
          summary={summary}
          scanInfo={scanInfo}
          totalControls={controls.length}
          expanded={expanded}
          onToggleSvc={handleToggleSvc}
          selCtrl={selCtrl}
          onCtrl={handleCtrl}
          hasSelection={!!(ctrl && grp)}
        />
        {/* Section 2: selected control — compliance mapping + risk assessment */}
        {ctrl && grp && (
          <div className="flex-1 min-w-0 overflow-hidden" style={{ background: mainBg }}>
            <ControlDetail ctrl={ctrl} group={grp} onBack={() => setSelCtrl(null)} />
          </div>
        )}
        {/* Section 3: account-level rollup summary */}
        <AccountSummaryPanel appId={selAppId} />
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 -m-4 sm:-m-5 lg:-m-7 overflow-hidden" style={{ background: mainBg }}>
      {/* Header with application selector */}
      <div
        className="flex items-center justify-between gap-3 px-4 py-3 border-b shrink-0 flex-wrap"
        style={{ background: panelBg, borderColor: borderClr }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className="p-2 rounded-xl shrink-0" style={{ background: "rgba(59,130,246,0.15)", border: "1px solid rgba(59,130,246,0.28)" }}>
            <Shield className="w-4 h-4 text-blue-400" />
          </div>
          <div className="min-w-0">
            <h1 className="text-sm font-bold text-[#0f172a] dark:text-white leading-tight">Architecture Compliance</h1>
            {scanInfo.sspName && (
              <p className="text-xs text-[#64748b] dark:text-gray-400 leading-tight">{scanInfo.sspName}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {loading && <Loader2 className="w-4 h-4 animate-spin text-blue-400" />}
          <AppDropdown apps={apps} value={selAppId} onChange={setSelAppId} loading={appsLoading} />
        </div>
      </div>

      {body}
    </div>
  );
}